import type {
  AllegianceDefinition,
  ContractOfferDefinition,
  CrewDefinition,
  EncounterDefinition,
  ShipDefinition,
  UpgradeDefinition,
} from './types';

export const SHIPS: Record<'cutter' | 'pirate', ShipDefinition> = {
  cutter: {
    id: 'cutter',
    name: 'The Moonglass',
    modelUrl: '/assets/models/elven-cutter.glb',
    fallbackColor: 0xd9d4bd,
    maxHull: 100,
    maxSails: 100,
    maxSpeed: 24,
    acceleration: 7.5,
    turnRate: 0.72,
    reload: 4.2,
    cannonDamage: 16,
    collisionRadius: 6,
    modelScale: 42,
    modelYaw: Math.PI / 2,
    waterline: -0.2,
  },
  pirate: {
    id: 'pirate',
    name: 'The Red Wraith',
    modelUrl: '/assets/models/pirate-junk.glb',
    fallbackColor: 0x692f2c,
    maxHull: 86,
    maxSails: 75,
    maxSpeed: 18,
    acceleration: 5.2,
    turnRate: 0.56,
    reload: 5.3,
    cannonDamage: 11,
    collisionRadius: 6.5,
    modelScale: 46,
    modelYaw: Math.PI / 2,
    waterline: -0.45,
  },
};

export const UPGRADES: UpgradeDefinition[] = [
  {
    id: 'quickfuse',
    name: 'Quickfuse Deck',
    description: 'Broadside reloads are 15% faster.',
    price: 100,
    icon: '✹',
  },
  {
    id: 'reinforced',
    name: 'Ironbark Ribs',
    description: 'Maximum hull integrity increases by 20%.',
    price: 100,
    icon: '⬡',
  },
  {
    id: 'runed-rigging',
    name: 'Runed Rigging',
    description: 'Top speed rises 10%; blink returns 15% sooner.',
    price: 100,
    icon: '⌁',
  },
];

export const VERDANT_LIGHTS: EncounterDefinition = {
  id: 'verdant-lights',
  title: 'Lights Beneath the Wake',
  body: 'Pale green lanterns move far below the keel—too evenly spaced to be fish, too deep to be divers.',
  investigateLabel: 'Heave to and investigate',
  ignoreLabel: 'Keep to the contract',
  relicWeight: 0.7,
};

export const ALLEGIANCES: AllegianceDefinition[] = [
  {
    id: 'free-captains',
    name: 'The Free Captains',
    shortName: 'Free Captains',
    motto: 'No crown beyond the horizon.',
    description: 'Smugglers, explorers, and private captains who answer to the articles they sign—and little else.',
    benefit: '+25% contract gold',
    sigil: '⚓',
    color: '#9b3f3f',
  },
  {
    id: 'verdant-court',
    name: 'The Verdant Court',
    shortName: 'Verdant Court',
    motto: 'The wind remembers its first name.',
    description: 'Elven tidewardens who chart living currents and keep the old sea roads from vanishing.',
    benefit: '+8% sailing speed',
    sigil: '✦',
    color: '#386a58',
  },
  {
    id: 'iron-compact',
    name: 'The Iron Compact',
    shortName: 'Iron Compact',
    motto: 'Stone below. Iron beside.',
    description: 'Dwarven shipwrights and harbor sentinels sworn to make civilization hold against the Blackwake.',
    benefit: '+10 hull, 15% less damage',
    sigil: '⬡',
    color: '#8a6337',
  },
];

export const CREW: CrewDefinition[] = [
  {
    id: 'mara-vane',
    name: 'Mara Vane',
    role: 'Master Gunner',
    epithet: 'The Powder Saint',
    description: 'A former royal artillerist who can read the roll of a deck like sheet music.',
    abilityName: 'Rolling Thunder',
    benefit: '18% faster reloads and 12% heavier broadsides',
    sigil: '✹',
  },
  {
    id: 'ilyra-moss',
    name: 'Ilyra Moss',
    role: 'Tidewitch',
    epithet: 'Green-Eyed Navigator',
    description: 'She hears paths beneath the water and folds short distances when the sea is listening.',
    abilityName: 'Shortcut Through Nowhere',
    benefit: 'Blink recharges 25% faster and reaches 6m farther',
    sigil: '◉',
  },
  {
    id: 'brann-oakhand',
    name: 'Brann Oakhand',
    role: 'Bosun',
    epithet: 'Last Off the Wreck',
    description: 'A patient carpenter with a talent for keeping shattered hulls angry and afloat.',
    abilityName: 'Hold Her Together',
    benefit: '+20 hull and +15 sail integrity',
    sigil: '⚒',
  },
];

export const CONTRACT_OFFERS: ContractOfferDefinition[] = [
  {
    id: 'wraith-hunt',
    title: 'Escort the Spice Convoy',
    subtitle: 'Merchant escort · Saltspire Cay',
    destination: 'Saltspire Cay',
    description: 'A spice convoy is making the northern passage. Pirate sails have been seen near Lantern Shoal; keep the merchants safe and ensure delivery.',
    difficulty: 2,
    distance: '4.8 nm',
    reward: 125,
    reputation: 110,
    icon: '☠',
  },
  {
    id: 'moon-seal',
    title: 'Relic Recovery at Ashen Reef',
    subtitle: 'Relic recovery · Ashen Reef',
    destination: 'Ashen Reef',
    description: 'A magistrate’s ward-seal vanished aboard the Wraith’s latest prize. Search the green lights, sink the thief, and recover the strongbox.',
    difficulty: 3,
    distance: '6.2 nm',
    reward: 165,
    reputation: 150,
    icon: '✦',
  },
  {
    id: 'shoal-gauntlet',
    title: 'Hunt the Black Gull',
    subtitle: 'Privateer warrant · Gloomreach',
    destination: 'Gloomreach',
    description: 'The Black Gull has been raiding the ghost-water approaches. Find her beyond the false lights and break her hold on the northern lane.',
    difficulty: 4,
    distance: '7.1 nm',
    reward: 210,
    reputation: 190,
    icon: '⌁',
  },
  {
    id: 'gravewatch-medicine',
    title: 'Deliver Medicine to Greywatch',
    subtitle: 'Urgent delivery · Greywatch',
    destination: 'Greywatch',
    description: 'Greywatch has sent an urgent plague flag. Carry sealed medicine through the Blackwake before the harbor bells fall silent.',
    difficulty: 1,
    distance: '5.1 nm',
    reward: 95,
    reputation: 90,
    icon: '⚗',
  },
];
